import { useState, useEffect, useCallback } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  Typography, TextField, IconButton, Switch, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Divider, Grid, Chip, Paper, Collapse,
} from '@mui/material';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { HistoryButton } from '../../components/EntityHistory';
import { Add, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import { FormikTextField } from '../../components/FormikFields';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import {
  postbacksService, Postback, PostbackUrlConfig, PostbackFilter, POSTBACK_TOKENS,
  PostbackFacebookConfig, PostbackGoogleAdsConfig, PostbackBingConfig, PostbackTwitterConfig, PostbackOpenAiConfig,
} from '../../services/postbacks.service';
import { conversionTypesService } from '../../services/conversion-types.service';
import EventMappingEditor from './EventMappingEditor';
import PostbackFilterEditor from './PostbackFilterEditor';

// '' is valid — an empty body/headers field means "none".
function jsonError(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  try { JSON.parse(t); return null; } catch (e) { return `Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`; }
}

// Headers must be a flat object of string values — a non-string value throws when substituted server-side.
function headersError(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  let v: unknown;
  try { v = JSON.parse(t); } catch (e) { return `Invalid JSON: ${e instanceof Error ? e.message : 'parse error'}`; }
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return 'Headers must be a JSON object';
  if (Object.values(v as Record<string, unknown>).some(val => typeof val !== 'string')) return 'Header values must be strings';
  return null;
}

// ── Local types ──

interface LocalUrlEntry {
  _id: string;
  event_types: string[];
  url: string;
  method: 'GET' | 'POST';
  bodyText: string;      // raw JSON text the user edits; parsed to `body` on save (POST only)
  headersText: string;   // raw JSON text; parsed to `headers` on save (POST only)
  event_mapping?: Record<string, string>;
  expanded: boolean;
  filter?: PostbackFilter;
}

interface LocalFbEntry {
  _id: string;
  event_types: string[];
  event_mapping?: Record<string, string>;
  pixel_id: string;
  access_token: string;
  default_event_name: string;
  expanded: boolean;
  test_event_code: string;
  test_result: { success: boolean; message: string } | null;
  testing: boolean;
  filter?: PostbackFilter;
}

interface LocalBingEntry {
  _id: string;
  event_types: string[];
  tag_id: string;
  api_token: string;
  event_mapping?: Record<string, string>;
  expanded: boolean;
  filter?: PostbackFilter;
}

interface LocalTwitterEntry {
  _id: string;
  event_types: string[];
  pixel_id: string;
  pixel_token: string;
  currency?: string;
  event_mapping?: Record<string, string>;
  expanded: boolean;
  filter?: PostbackFilter;
  test_event: string;
  testing: boolean;
  test_result: { success: boolean; message: string } | null;
}

interface LocalOpenAiEntry {
  _id: string;
  event_types: string[];
  pid: string;
  api_key: string;
  default_event_type: string;
  event_mapping?: Record<string, string>;
  expanded: boolean;
  filter?: PostbackFilter;
  testing: boolean;
  test_result: { success: boolean; message: string } | null;
}

interface LocalGoogleAdsEntry {
  _id: string;
  event_types: string[];
  login_customer_id: string;
  customer_id: string;
  conversion_action_id: string;
  refresh_token: string;
  event_mapping?: Record<string, string>;
  expanded: boolean;
  testing: boolean;
  test_result: { success: boolean; message: string } | null;
  filter?: PostbackFilter;
}

// ── Constants ──

const FB_STANDARD_EVENTS = [
  'Purchase', 'Lead', 'CompleteRegistration', 'Subscribe', 'StartTrial',
  'InitiateCheckout', 'AddToCart', 'ViewContent', 'Search', 'Contact',
];

const schema = Yup.object({ name: Yup.string().trim().required('Name is required') });

// ── Event Types Chip Input ──

function EventTypesInput({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const [input, setInput] = useState('');
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.5, py: 0.3, minHeight: 36 }}>
      {values.map((v, i) => (
        <Chip key={i} label={v} size="small" onDelete={() => onChange(values.filter((_, j) => j !== i))} sx={{ height: 24, fontSize: '0.8rem' }} />
      ))}
      <TextField
        size="small" variant="standard" placeholder={values.length ? '+ add' : 'All events (type + enter to filter)'}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && input.trim()) {
            e.preventDefault();
            if (!values.includes(input.trim())) onChange([...values, input.trim()]);
            setInput('');
          }
        }}
        sx={{ minWidth: 120, flex: 1, '& input': { fontSize: '0.8rem', py: 0.3 } }}
        slotProps={{ input: { disableUnderline: true } }}
      />
    </Box>
  );
}

export default function CreateEditPostback() {
  const { modalState, toggleModal } = useModalState('postback');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['postback', modalState.id],
    queryFn: () => postbacksService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  const { data: conversionTypes } = useQuery({
    queryKey: ['conversion-types'],
    queryFn: () => conversionTypesService.query({ limit: 200 }),
    enabled: modalState.open,
  });
  const conversionTypeNames = (conversionTypes?.data || []).map(ct => ct.name);

  const isEdit = modalState.reason === 'Update' && !!entity;
  const isDuplicate = modalState.reason === 'Duplicate';
  const title = isEdit ? `Edit: ${entity?.name}` : isDuplicate ? 'Duplicate Postback' : 'Create Postback';

  const [type, setType] = useState<'url' | 'facebook_capi' | 'google_ads' | 'bing_capi' | 'twitter_capi' | 'openai_capi'>('url');
  const [enabled, setEnabled] = useState(true);
  const [urlEntries, setUrlEntries] = useState<LocalUrlEntry[]>([]);
  const [fbEntries, setFbEntries] = useState<LocalFbEntry[]>([]);
  const [googleEntries, setGoogleEntries] = useState<LocalGoogleAdsEntry[]>([]);
  const [bingEntries, setBingEntries] = useState<LocalBingEntry[]>([]);
  const [twitterEntries, setTwitterEntries] = useState<LocalTwitterEntry[]>([]);
  const [openaiEntries, setOpenaiEntries] = useState<LocalOpenAiEntry[]>([]);
  const [workspaceId, setWorkspaceId] = useState('');

  // Load entity into local state
  useEffect(() => {
    if (entity && modalState.open) {
      // A duplicate defaults to the source's workspace; admins can retarget via the picker.
      setWorkspaceId((entity as any).workspace_id ?? defaultCreateWorkspace());
      setType(entity.type);
      if (entity.type === 'url') {
        const cfg = entity.config as PostbackUrlConfig;
        setEnabled(cfg.enabled ?? true);
        if (cfg.entries && cfg.entries.length > 0) {
          setUrlEntries(cfg.entries.map(e => ({
            _id: crypto.randomUUID(),
            event_types: e.event_types || [],
            event_mapping: e.event_mapping,
            filter: e.filter,
            url: e.url,
            method: e.method === 'POST' ? 'POST' : 'GET',
            bodyText: e.body != null ? JSON.stringify(e.body, null, 2) : '',
            headersText: e.headers != null ? JSON.stringify(e.headers, null, 2) : '',
            expanded: true,
          })));
        } else {
          setUrlEntries([]);
        }
      } else if (entity.type === 'bing_capi') {
        const cfg = entity.config as PostbackBingConfig;
        setEnabled(true);
        setBingEntries((cfg.entries || []).map(e => ({
          _id: crypto.randomUUID(),
          event_types: e.event_types || [],
          event_mapping: e.event_mapping,
          filter: e.filter,
          tag_id: e.tag_id || '',
          api_token: e.api_token || '',
          expanded: true,
        })));
      } else if (entity.type === 'twitter_capi') {
        const cfg = entity.config as PostbackTwitterConfig;
        setEnabled(true);
        setTwitterEntries((cfg.entries || []).map(e => ({
          _id: crypto.randomUUID(),
          event_types: e.event_types || [],
          event_mapping: e.event_mapping,
          filter: e.filter,
          pixel_id: e.pixel_id || '',
          pixel_token: e.pixel_token || '',
          currency: e.currency || '',
          expanded: true,
          test_event: '', testing: false, test_result: null,
        })));
      } else if (entity.type === 'openai_capi') {
        const cfg = entity.config as PostbackOpenAiConfig;
        setEnabled(true);
        setOpenaiEntries((cfg.entries || []).map(e => ({
          _id: crypto.randomUUID(),
          event_types: e.event_types || [],
          event_mapping: e.event_mapping,
          filter: e.filter,
          pid: e.pid || '',
          api_key: e.api_key || '',
          default_event_type: e.default_event_type || '',
          expanded: true,
          testing: false, test_result: null,
        })));
      } else if (entity.type === 'google_ads') {
        const cfg = entity.config as PostbackGoogleAdsConfig;
        setEnabled(true);
        if (cfg.entries && cfg.entries.length > 0) {
          setGoogleEntries(cfg.entries.map(e => ({
            _id: crypto.randomUUID(),
            event_types: e.event_types || [],
            event_mapping: e.event_mapping,
            filter: e.filter,
            login_customer_id: e.login_customer_id || '',
            customer_id: e.customer_id,
            conversion_action_id: e.conversion_action_id,
            refresh_token: e.refresh_token,
            expanded: true,
            testing: false, test_result: null,
          })));
        } else {
          setGoogleEntries([]);
        }
      } else {
        const cfg = entity.config as PostbackFacebookConfig;
        setEnabled(true);
        if (cfg.entries && cfg.entries.length > 0) {
          setFbEntries(cfg.entries.map(e => ({
            _id: crypto.randomUUID(),
            event_types: e.event_types || [],
            filter: e.filter,
            pixel_id: e.pixel_id,
            access_token: e.access_token,
            default_event_name: e.default_event_name || 'Purchase',
            event_mapping: e.event_mapping,
            expanded: true, test_event_code: '', test_result: null, testing: false,
          })));
        } else {
          setFbEntries([]);
        }
      }
    } else if (!modalState.id && modalState.open) {
      setWorkspaceId(defaultCreateWorkspace());
      setType('url');
      setEnabled(true);
      setUrlEntries([{ _id: crypto.randomUUID(), event_types: [], url: '', method: 'GET', bodyText: '', headersText: '', expanded: true }]);
      setFbEntries([]);
      setGoogleEntries([]);
      setBingEntries([]);
      setTwitterEntries([]);
      setOpenaiEntries([]);
    }
  }, [entity, modalState.open, modalState.id]);

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const initialValues = (() => {
    if (!entity) return { name: '' };
    const base = { name: entity.name };
    return base;
  })();

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="md" fullWidth title={isLoading ? 'Loading...' : title} titleId="postback-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            try {
              setSubmitted();
              const payload: Partial<Postback> = { name: values.name, type, ...(isEdit ? {} : { workspace_id: workspaceId }) };

              if (type === 'url') {
                const parseJson = (label: string, text: string): unknown => {
                  const t = text.trim();
                  if (!t) return undefined;
                  try { return JSON.parse(t); } catch { throw new Error(`${label} is not valid JSON`); }
                };
                payload.config = {
                  enabled,
                  entries: urlEntries.map(e => {
                    const isPost = e.method === 'POST';
                    if (isPost) { const he = headersError(e.headersText); if (he) throw new Error(`Headers: ${he}`); }
                    const headers = isPost ? parseJson('Headers', e.headersText) : undefined;
                    return {
                      event_types: e.event_types,
                      url: e.url,
                      ...(isPost && { method: 'POST' as const, body: parseJson('POST body', e.bodyText) }),
                      ...(headers !== undefined && { headers }),
                      ...(e.event_mapping && { event_mapping: e.event_mapping }),
                      ...(e.filter && { filter: e.filter }),
                    };
                  }),
                } as PostbackUrlConfig;
              } else if (type === 'bing_capi') {
                payload.config = {
                  entries: bingEntries.map(e => ({
                    event_types: e.event_types,
                    tag_id: e.tag_id.trim(),
                    api_token: e.api_token.trim(),
                    ...(e.event_mapping && { event_mapping: e.event_mapping }),
                    ...(e.filter && { filter: e.filter }),
                  })),
                } as PostbackBingConfig;
              } else if (type === 'twitter_capi') {
                payload.config = {
                  entries: twitterEntries.map(e => ({
                    event_types: e.event_types,
                    pixel_id: e.pixel_id.trim(),
                    pixel_token: e.pixel_token.trim(),
                    ...(e.currency?.trim() && { currency: e.currency.trim().toUpperCase() }),
                    ...(e.event_mapping && { event_mapping: e.event_mapping }),
                    ...(e.filter && { filter: e.filter }),
                  })),
                } as PostbackTwitterConfig;
              } else if (type === 'openai_capi') {
                payload.config = {
                  entries: openaiEntries.map(e => ({
                    event_types: e.event_types,
                    pid: e.pid.trim(),
                    api_key: e.api_key.trim(),
                    ...(e.default_event_type.trim() && { default_event_type: e.default_event_type.trim() }),
                    ...(e.event_mapping && { event_mapping: e.event_mapping }),
                    ...(e.filter && { filter: e.filter }),
                  })),
                } as PostbackOpenAiConfig;
              } else if (type === 'google_ads') {
                payload.config = {
                  entries: googleEntries.map(e => ({
                    event_types: e.event_types,
                    login_customer_id: e.login_customer_id || undefined,
                    customer_id: e.customer_id,
                    conversion_action_id: e.conversion_action_id,
                    refresh_token: e.refresh_token,
                    ...(e.event_mapping && { event_mapping: e.event_mapping }),
                    ...(e.filter && { filter: e.filter }),
                  })),
                } as PostbackGoogleAdsConfig;
              } else {
                payload.config = {
                  entries: fbEntries.map(e => ({
                    event_types: e.event_types,
                    pixel_id: e.pixel_id,
                    access_token: e.access_token,
                    default_event_name: e.default_event_name,
                    ...(e.event_mapping && { event_mapping: e.event_mapping }),
                    ...(e.filter && { filter: e.filter }),
                  })),
                } as PostbackFacebookConfig;
              }

              if (isEdit) {
                await postbacksService.update(entity!.id, payload);
              } else {
                await postbacksService.create(payload);
              }
              queryClient.invalidateQueries({ queryKey: ['postbacks'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status }: { submitForm: () => Promise<void>; isSubmitting: boolean; status?: string }) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CopyId id={entity?.id} show={isEdit} />
                  <Box sx={{ flex: 1 }} />
                  {isEdit && <HistoryButton entityType="postbacks" entityId={entity!.id} entityName={entity?.name} />}
                </Box>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormikTextField name="name" label="Name" fullWidth size="small" />
                  {!isEdit && (
                    <WorkspaceField value={workspaceId} onChange={setWorkspaceId} />
                  )}

                  <ToggleButtonGroup value={type} exclusive onChange={(_, v) => { if (v) setType(v); }} size="small" fullWidth>
                    <ToggleButton value="url">URL Postback</ToggleButton>
                    <ToggleButton value="facebook_capi">Facebook CAPI</ToggleButton>
                    <ToggleButton value="google_ads">Google Ads</ToggleButton>
                    <ToggleButton value="bing_capi">Bing CAPI</ToggleButton>
                    <ToggleButton value="twitter_capi">X (Twitter)</ToggleButton>
                    <ToggleButton value="openai_capi">OpenAI Ads</ToggleButton>
                  </ToggleButtonGroup>

                  {type === 'url' && (
                    <>
                      <FormControlLabel
                        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                        label="Enabled"
                      />

                      <Typography variant="subtitle2">
                        URL Entries ({urlEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: -1 }}>
                        Each entry fires for its event types. Empty = fires for all events. All matching entries fire.
                      </Typography>

                      {urlEntries.map((entry) => (
                        <Paper key={entry._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.8,
                            bgcolor: 'action.hover', cursor: 'pointer',
                          }} onClick={() => setUrlEntries(prev => prev.map(e => e._id === entry._id ? { ...e, expanded: !e.expanded } : e))}>
                            {entry.expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                              {entry.event_types.length > 0 ? entry.event_types.join(', ') : 'All Events'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {entry.url || '(no URL)'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setUrlEntries(prev => prev.filter(ue => ue._id !== entry._id)); }} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={entry.expanded}>
                            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Event Types (empty = all events):</Typography>
                                <EventTypesInput
                                  values={entry.event_types}
                                  onChange={(v) => setUrlEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_types: v } : e))}
                                />
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box>
                                  <EventMappingEditor
                                    value={entry.event_mapping}
                                    onChange={(m) => setUrlEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_mapping: m } : e))}
                                    conversionTypeNames={conversionTypeNames}
                                    label="Event mapping"
                                    type={type}
                                  />
                              </Box>
                              <Box>
                                <PostbackFilterEditor
                                  value={entry.filter}
                                  onChange={(f) => setUrlEntries(prev => prev.map(e => e._id === entry._id ? { ...e, filter: f } : e))}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">Method:</Typography>
                                <ToggleButtonGroup
                                  size="small" exclusive value={entry.method}
                                  onChange={(_e, v) => { if (v) setUrlEntries(prev => prev.map(ue => ue._id === entry._id ? { ...ue, method: v } : ue)); }}
                                >
                                  <ToggleButton value="GET">GET</ToggleButton>
                                  <ToggleButton value="POST">POST</ToggleButton>
                                </ToggleButtonGroup>
                              </Box>
                              <TextField
                                label={entry.method === 'POST' ? 'Endpoint URL' : 'Postback URL'} fullWidth multiline minRows={2} size="small"
                                value={entry.url}
                                onChange={(e) => setUrlEntries(prev => prev.map(ue => ue._id === entry._id ? { ...ue, url: e.target.value } : ue))}
                                placeholder="https://network.com/postback?cid={{click_id}}&rev={{revenue}}&txid={{transaction_id}}"
                                helperText="{{token}} placeholders — see available tokens below"
                              />
                              {entry.method === 'POST' && (
                                <>
                                  <TextField
                                    label="POST body (JSON)" fullWidth multiline minRows={5} size="small"
                                    value={entry.bodyText}
                                    onChange={(e) => setUrlEntries(prev => prev.map(ue => ue._id === entry._id ? { ...ue, bodyText: e.target.value } : ue))}
                                    placeholder={'{\n  "event_name": "purchase",\n  "click_id": "{{external_id}}",\n  "properties": { "value": "{{revenue}}", "currency": "{{currency}}" }\n}'}
                                    error={!!jsonError(entry.bodyText)}
                                    helperText={jsonError(entry.bodyText) || "JSON sent as the body. A leaf that is exactly one token keeps the value's type (numeric {{revenue}} stays a number); a null value drops the key."}
                                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
                                  />
                                  <TextField
                                    label="Headers (JSON, optional)" fullWidth multiline minRows={2} size="small"
                                    value={entry.headersText}
                                    onChange={(e) => setUrlEntries(prev => prev.map(ue => ue._id === entry._id ? { ...ue, headersText: e.target.value } : ue))}
                                    placeholder={'{ "Authorization": "Bearer {{...}}" }'}
                                    error={!!headersError(entry.headersText)}
                                    helperText={headersError(entry.headersText) || "Content-Type: application/json is sent unless you set one. Header values are strings, token-substituted."}
                                    slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: '0.8rem' } } }}
                                  />
                                </>
                              )}
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  Available tokens (event_time is unix seconds):
                                </Typography>
                                {POSTBACK_TOKENS.map(g => (
                                  <Typography key={g.group} variant="caption" color="text.secondary" component="div"
                                    sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>
                                    {g.group}: {g.tokens.map(t => `{{${t}}}`).join(' ')}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          </Collapse>
                        </Paper>
                      ))}

                      <Button variant="outlined" size="small" startIcon={<Add />} fullWidth
                        onClick={() => setUrlEntries(prev => [...prev, { _id: crypto.randomUUID(), event_types: [], url: '', method: 'GET', bodyText: '', headersText: '', expanded: true }])}>
                        Add URL Entry
                      </Button>
                    </>
                  )}

                  {type === 'facebook_capi' && (
                    <>
                      <Typography variant="subtitle2">
                        Facebook CAPI Entries ({fbEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: -1 }}>
                        Each entry fires for its event types with its own pixel. Empty = fires for all events.
                      </Typography>

                      {fbEntries.map((entry) => (
                        <Paper key={entry._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.8,
                            bgcolor: 'action.hover', cursor: 'pointer',
                          }} onClick={() => setFbEntries(prev => prev.map(e => e._id === entry._id ? { ...e, expanded: !e.expanded } : e))}>
                            {entry.expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                              {entry.event_types.length > 0 ? entry.event_types.join(', ') : 'All Events'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Pixel: {entry.pixel_id || '(none)'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setFbEntries(prev => prev.filter(fe => fe._id !== entry._id)); }} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={entry.expanded}>
                            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Event Types (empty = all events):</Typography>
                                <EventTypesInput
                                  values={entry.event_types}
                                  onChange={(v) => setFbEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_types: v } : e))}
                                />
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box>
                                  <EventMappingEditor
                                    value={entry.event_mapping}
                                    onChange={(m) => setFbEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_mapping: m } : e))}
                                    conversionTypeNames={conversionTypeNames}
                                  outboundOptions={FB_STANDARD_EVENTS}
                                    label="Event mapping"
                                    type={type}
                                  />
                              </Box>
                              <Box>
                                <PostbackFilterEditor
                                  value={entry.filter}
                                  onChange={(f) => setFbEntries(prev => prev.map(e => e._id === entry._id ? { ...e, filter: f } : e))}
                                />
                              </Box>
                              <TextField label="Pixel ID" fullWidth size="small" value={entry.pixel_id}
                                onChange={(e) => setFbEntries(prev => prev.map(fe => fe._id === entry._id ? { ...fe, pixel_id: e.target.value } : fe))} />
                              <TextField label="Access Token" fullWidth size="small" value={entry.access_token}
                                onChange={(e) => setFbEntries(prev => prev.map(fe => fe._id === entry._id ? { ...fe, access_token: e.target.value } : fe))} />
                              <TextField label="Default Event Name" fullWidth size="small" value={entry.default_event_name}
                                onChange={(e) => setFbEntries(prev => prev.map(fe => fe._id === entry._id ? { ...fe, default_event_name: e.target.value } : fe))} />

                              {/* Test Pixel */}
                              <Divider sx={{ my: 0.5 }} />
                              <Typography variant="caption" color="text.secondary">Test Connection</Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField label="Test Event Code" size="small" value={entry.test_event_code}
                                  onChange={(e) => setFbEntries(prev => prev.map(fe => fe._id === entry._id ? { ...fe, test_event_code: e.target.value } : fe))}
                                  placeholder="From FB Events Manager → Test Events"
                                  sx={{ flex: 1 }} />
                                <Button
                                  variant="outlined" size="small"
                                  disabled={!entry.pixel_id || !entry.access_token || entry.testing}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setFbEntries(prev => prev.map(fe => fe._id === entry._id ? { ...fe, testing: true, test_result: null } : fe));
                                    try {
                                      const result = await postbacksService.testFbPixel({
                                        pixel_id: entry.pixel_id,
                                        access_token: entry.access_token,
                                        test_event_code: entry.test_event_code || undefined,
                                        event_name: entry.default_event_name || 'Purchase',
                                      });
                                      setFbEntries(prev => prev.map(fe => fe._id === entry._id ? {
                                        ...fe, testing: false,
                                        test_result: {
                                          success: result.success,
                                          message: result.success
                                            ? `Events received: ${result.data?.events_received || 0}`
                                            : (result.data?.error || 'Test failed'),
                                        },
                                      } : fe));
                                    } catch (err: any) {
                                      setFbEntries(prev => prev.map(fe => fe._id === entry._id ? {
                                        ...fe, testing: false,
                                        test_result: { success: false, message: err.message || 'Test failed' },
                                      } : fe));
                                    }
                                  }}
                                  sx={{ minWidth: 110, height: 40 }}
                                >
                                  {entry.testing ? <CircularProgress size={16} /> : 'Test Pixel'}
                                </Button>
                              </Box>
                              {entry.test_result && (
                                <Alert severity={entry.test_result.success ? 'success' : 'error'} sx={{ py: 0 }}>
                                  {entry.test_result.message}
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      ))}

                      <Button variant="outlined" size="small" startIcon={<Add />} fullWidth
                        onClick={() => setFbEntries(prev => [...prev, {
                          _id: crypto.randomUUID(), event_types: [], pixel_id: '', access_token: '',
                          default_event_name: 'Purchase', expanded: true,
                          test_event_code: '', test_result: null, testing: false,
                        }])}>
                        Add FB CAPI Entry
                      </Button>
                    </>
                  )}

                  {type === 'bing_capi' && (
                    <>
                      <Typography variant="subtitle2">
                        Bing CAPI Entries ({bingEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: -1 }}>
                        Sends conversions to Microsoft Advertising's Conversions API. Attribution needs
                        msclkid on the click, and the event name below must match a conversion goal.
                      </Typography>

                      {bingEntries.map((entry) => (
                        <Paper key={entry._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.8,
                            bgcolor: 'action.hover', cursor: 'pointer',
                          }} onClick={() => setBingEntries(prev => prev.map(e => e._id === entry._id ? { ...e, expanded: !e.expanded } : e))}>
                            {entry.expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                              {entry.event_types.length > 0 ? entry.event_types.join(', ') : 'All Events'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.tag_id || '(no tag ID)'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setBingEntries(prev => prev.filter(be => be._id !== entry._id)); }} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={entry.expanded}>
                            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Event Types (empty = all events):</Typography>
                                <EventTypesInput
                                  values={entry.event_types}
                                  onChange={(v) => setBingEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_types: v } : e))}
                                />
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box>
                                  <EventMappingEditor
                                    value={entry.event_mapping}
                                    onChange={(m) => setBingEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_mapping: m } : e))}
                                    conversionTypeNames={conversionTypeNames}
                                    label="Event mapping"
                                    type={type}
                                  />
                              </Box>
                              <Box>
                                <PostbackFilterEditor
                                  value={entry.filter}
                                  onChange={(f) => setBingEntries(prev => prev.map(e => e._id === entry._id ? { ...e, filter: f } : e))}
                                />
                              </Box>
                              <Grid container spacing={1}>
                                <Grid size={{ xs: 4 }}>
                                  <TextField label="UET Tag ID" fullWidth size="small" value={entry.tag_id}
                                    onChange={(e) => setBingEntries(prev => prev.map(be => be._id === entry._id ? { ...be, tag_id: e.target.value } : be))}
                                    placeholder="187266494" helperText="Numeric, from the UET tag" />
                                </Grid>
                                <Grid size={{ xs: 8 }}>
                                  <TextField label="CAPI Token" fullWidth size="small" type="password" value={entry.api_token}
                                    onChange={(e) => setBingEntries(prev => prev.map(be => be._id === entry._id ? { ...be, api_token: e.target.value } : be))}
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    helperText="From the tag's Conversions API setup — not the developer token" />
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </Paper>
                      ))}

                      <Button size="small" variant="outlined" startIcon={<Add />}
                        onClick={() => setBingEntries(prev => [...prev, {
                          _id: crypto.randomUUID(), event_types: [], tag_id: '', api_token: '', expanded: true,
                        }])}>
                        Add Bing CAPI Entry
                      </Button>
                    </>
                  )}

                  {type === 'twitter_capi' && (
                    <>
                      <Typography variant="subtitle2">
                        X (Twitter) Conversion API Entries ({twitterEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: -1 }}>
                        Sends conversions to X's Conversion API via OAuth 1.0a. The event name below maps to the X conversion event.
                      </Typography>

                      {twitterEntries.map((entry) => (
                        <Paper key={entry._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.8,
                            bgcolor: 'action.hover', cursor: 'pointer',
                          }} onClick={() => setTwitterEntries(prev => prev.map(e => e._id === entry._id ? { ...e, expanded: !e.expanded } : e))}>
                            {entry.expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                              {entry.event_types.length > 0 ? entry.event_types.join(', ') : 'All Events'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.pixel_id || '(no pixel ID)'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setTwitterEntries(prev => prev.filter(te => te._id !== entry._id)); }} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={entry.expanded}>
                            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Event Types (empty = all events):</Typography>
                                <EventTypesInput
                                  values={entry.event_types}
                                  onChange={(v) => setTwitterEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_types: v } : e))}
                                />
                              </Box>
                              <Divider sx={{ my: 0.5 }} />
                              <Box>
                                <EventMappingEditor
                                  value={entry.event_mapping}
                                  onChange={(m) => setTwitterEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_mapping: m } : e))}
                                  conversionTypeNames={conversionTypeNames}
                                  label="Event mapping"
                                  type={type}
                                />
                              </Box>
                              <Box>
                                <PostbackFilterEditor
                                  value={entry.filter}
                                  onChange={(f) => setTwitterEntries(prev => prev.map(e => e._id === entry._id ? { ...e, filter: f } : e))}
                                />
                              </Box>
                              <Grid container spacing={1}>
                                <Grid size={{ xs: 12 }}>
                                  <TextField label="Pixel / Tag ID" fullWidth size="small" value={entry.pixel_id}
                                    onChange={(e) => setTwitterEntries(prev => prev.map(te => te._id === entry._id ? { ...te, pixel_id: e.target.value } : te))}
                                    helperText="The X pixel id used in the conversions endpoint" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <TextField label="Pixel Token" fullWidth size="small" type="password" value={entry.pixel_token}
                                    onChange={(e) => setTwitterEntries(prev => prev.map(te => te._id === entry._id ? { ...te, pixel_token: e.target.value } : te))}
                                    helperText="Sent as the X-Pixel-Token header" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <TextField label="Account currency" fullWidth size="small" value={entry.currency ?? ''}
                                    onChange={(e) => setTwitterEntries(prev => prev.map(te => te._id === entry._id ? { ...te, currency: e.target.value } : te))}
                                    placeholder="e.g. ILS"
                                    helperText="If the X ad account bills in a non-USD currency, set it here — values are converted from USD before sending. Leave blank for USD." />
                                </Grid>
                              </Grid>

                              <Divider sx={{ my: 0.5 }} />
                              <Typography variant="caption" color="text.secondary">
                                Test Connection — fires with a sample click id, so it verifies the call without logging a real conversion.
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField label="Test event id" size="small" value={entry.test_event}
                                  onChange={(e) => setTwitterEntries(prev => prev.map(te => te._id === entry._id ? { ...te, test_event: e.target.value } : te))}
                                  placeholder="X conversion event id (defaults to Purchase)"
                                  sx={{ flex: 1 }} />
                                <Button
                                  variant="outlined" size="small"
                                  disabled={!entry.pixel_id || !entry.pixel_token || entry.testing}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setTwitterEntries(prev => prev.map(te => te._id === entry._id ? { ...te, testing: true, test_result: null } : te));
                                    try {
                                      const result = await postbacksService.testTwitter({
                                        pixel_id: entry.pixel_id,
                                        pixel_token: entry.pixel_token,
                                        event_name: entry.test_event || undefined,
                                      });
                                      setTwitterEntries(prev => prev.map(te => te._id === entry._id ? {
                                        ...te, testing: false,
                                        test_result: {
                                          success: result.success,
                                          message: result.success
                                            ? `Accepted (HTTP ${result.data?.http_status ?? 200})`
                                            : (result.data?.error || 'Test failed'),
                                        },
                                      } : te));
                                    } catch (err: any) {
                                      setTwitterEntries(prev => prev.map(te => te._id === entry._id ? {
                                        ...te, testing: false,
                                        test_result: { success: false, message: err.message || 'Test failed' },
                                      } : te));
                                    }
                                  }}
                                  sx={{ minWidth: 110, height: 40 }}
                                >
                                  {entry.testing ? <CircularProgress size={16} /> : 'Send Test'}
                                </Button>
                              </Box>
                              {entry.test_result && (
                                <Alert severity={entry.test_result.success ? 'success' : 'error'} sx={{ py: 0 }}>
                                  {entry.test_result.message}
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      ))}

                      <Button size="small" variant="outlined" startIcon={<Add />}
                        onClick={() => setTwitterEntries(prev => [...prev, {
                          _id: crypto.randomUUID(), event_types: [], pixel_id: '', pixel_token: '', currency: '', expanded: true,
                          test_event: '', testing: false, test_result: null,
                        }])}>
                        Add X Conversion Entry
                      </Button>
                    </>
                  )}

                  {type === 'openai_capi' && (
                    <>
                      <Typography variant="subtitle2">
                        OpenAI Ads Conversions API Entries ({openaiEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: -1 }}>
                        Sends server-side conversions to OpenAI Ads. Attribution needs the click's <code>oppref</code> —
                        capture it on the OpenAI traffic source (external id type <code>oppref</code>).
                      </Typography>

                      {openaiEntries.map((entry) => (
                        <Paper key={entry._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.8,
                            bgcolor: 'action.hover', cursor: 'pointer',
                          }} onClick={() => setOpenaiEntries(prev => prev.map(e => e._id === entry._id ? { ...e, expanded: !e.expanded } : e))}>
                            {entry.expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                              {entry.event_types.length > 0 ? entry.event_types.join(', ') : 'All Events'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.pid || '(no pid)'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setOpenaiEntries(prev => prev.filter(oe => oe._id !== entry._id)); }} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={entry.expanded}>
                            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Event Types (empty = all events):</Typography>
                                <EventTypesInput
                                  values={entry.event_types}
                                  onChange={(v) => setOpenaiEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_types: v } : e))}
                                />
                              </Box>
                              <Divider sx={{ my: 0.5 }} />
                              <Box>
                                <EventMappingEditor
                                  value={entry.event_mapping}
                                  onChange={(m) => setOpenaiEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_mapping: m } : e))}
                                  conversionTypeNames={conversionTypeNames}
                                  label="Event mapping (conversion type → OpenAI event type)"
                                  type={type}
                                />
                              </Box>
                              <Box>
                                <PostbackFilterEditor
                                  value={entry.filter}
                                  onChange={(f) => setOpenaiEntries(prev => prev.map(e => e._id === entry._id ? { ...e, filter: f } : e))}
                                />
                              </Box>
                              <Grid container spacing={1}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <TextField label="Pixel ID (pid)" fullWidth size="small" value={entry.pid}
                                    onChange={(e) => setOpenaiEntries(prev => prev.map(oe => oe._id === entry._id ? { ...oe, pid: e.target.value } : oe))}
                                    helperText="Query param on the events endpoint" />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <TextField label="API Key" fullWidth size="small" type="password" value={entry.api_key}
                                    onChange={(e) => setOpenaiEntries(prev => prev.map(oe => oe._id === entry._id ? { ...oe, api_key: e.target.value } : oe))}
                                    helperText="Sent as the Bearer token" />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                  <TextField label="Default event type" fullWidth size="small" value={entry.default_event_type}
                                    onChange={(e) => setOpenaiEntries(prev => prev.map(oe => oe._id === entry._id ? { ...oe, default_event_type: e.target.value } : oe))}
                                    placeholder="order_created"
                                    helperText="Used when the mapping doesn't resolve a type" />
                                </Grid>
                              </Grid>

                              <Divider sx={{ my: 0.5 }} />
                              <Typography variant="caption" color="text.secondary">
                                Test Connection — a <code>validate_only</code> dry run: OpenAI validates the credentials and
                                payload but records nothing (so it won't appear in event monitoring).
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  variant="outlined" size="small"
                                  disabled={!entry.pid || !entry.api_key || entry.testing}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setOpenaiEntries(prev => prev.map(oe => oe._id === entry._id ? { ...oe, testing: true, test_result: null } : oe));
                                    try {
                                      const result = await postbacksService.testOpenai({
                                        pid: entry.pid,
                                        api_key: entry.api_key,
                                        event_type: entry.default_event_type || undefined,
                                      });
                                      setOpenaiEntries(prev => prev.map(oe => oe._id === entry._id ? {
                                        ...oe, testing: false,
                                        test_result: {
                                          success: result.success,
                                          message: result.success
                                            ? `Validated (HTTP ${result.data?.http_status ?? 200})`
                                            : (result.data?.error || 'Test failed'),
                                        },
                                      } : oe));
                                    } catch (err: any) {
                                      setOpenaiEntries(prev => prev.map(oe => oe._id === entry._id ? {
                                        ...oe, testing: false,
                                        test_result: { success: false, message: err.message || 'Test failed' },
                                      } : oe));
                                    }
                                  }}
                                  sx={{ minWidth: 110, height: 40 }}
                                >
                                  {entry.testing ? <CircularProgress size={16} /> : 'Validate'}
                                </Button>
                              </Box>
                              {entry.test_result && (
                                <Alert severity={entry.test_result.success ? 'success' : 'error'} sx={{ py: 0 }}>
                                  {entry.test_result.message}
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      ))}

                      <Button size="small" variant="outlined" startIcon={<Add />}
                        onClick={() => setOpenaiEntries(prev => [...prev, {
                          _id: crypto.randomUUID(), event_types: [], pid: '', api_key: '', default_event_type: 'order_created',
                          expanded: true, testing: false, test_result: null,
                        }])}>
                        Add OpenAI Ads Entry
                      </Button>
                    </>
                  )}

                  {type === 'google_ads' && (
                    <>
                      <Typography variant="subtitle2">
                        Google Ads Entries ({googleEntries.length})
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: -1 }}>
                        Each entry sends conversions to a Google Ads conversion action via the Data Manager API.
                      </Typography>

                      {googleEntries.map((entry) => (
                        <Paper key={entry._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.8,
                            bgcolor: 'action.hover', cursor: 'pointer',
                          }} onClick={() => setGoogleEntries(prev => prev.map(e => e._id === entry._id ? { ...e, expanded: !e.expanded } : e))}>
                            {entry.expanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}>
                              {entry.event_types.length > 0 ? entry.event_types.join(', ') : 'All Events'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.customer_id || '(no customer ID)'}
                            </Typography>
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setGoogleEntries(prev => prev.filter(ge => ge._id !== entry._id)); }} sx={{ p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Collapse in={entry.expanded}>
                            <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Event Types (empty = all events):</Typography>
                                <EventTypesInput
                                  values={entry.event_types}
                                  onChange={(v) => setGoogleEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_types: v } : e))}
                                />
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Box>
                                  <EventMappingEditor
                                    value={entry.event_mapping}
                                    onChange={(m) => setGoogleEntries(prev => prev.map(e => e._id === entry._id ? { ...e, event_mapping: m } : e))}
                                    conversionTypeNames={conversionTypeNames}
                                    label="Event mapping"
                                    type={type}
                                  />
                              </Box>
                              <Box>
                                <PostbackFilterEditor
                                  value={entry.filter}
                                  onChange={(f) => setGoogleEntries(prev => prev.map(e => e._id === entry._id ? { ...e, filter: f } : e))}
                                />
                              </Box>
                              <Grid container spacing={1}>
                                <Grid size={{ xs: 4 }}>
                                  <TextField label="Manager Account ID" fullWidth size="small" value={entry.login_customer_id}
                                    onChange={(e) => setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, login_customer_id: e.target.value } : ge))}
                                    placeholder="283-712-9442" helperText="MCC ID (if using MCC)" />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                  <TextField label="Customer ID" fullWidth size="small" value={entry.customer_id}
                                    onChange={(e) => setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, customer_id: e.target.value } : ge))}
                                    placeholder="185-616-7549" />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                  <TextField label="Conversion Action ID" fullWidth size="small" value={entry.conversion_action_id}
                                    onChange={(e) => setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, conversion_action_id: e.target.value } : ge))}
                                    placeholder="7673975220" />
                                </Grid>
                              </Grid>
                              <Divider sx={{ my: 0.5 }} />
                              <Typography variant="caption" color="text.secondary">Google Account</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {entry.refresh_token ? (
                                  <>
                                    <Chip label="Connected" color="success" size="small" variant="outlined" />
                                    <Button size="small" color="error" variant="text"
                                      onClick={() => setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, refresh_token: '' } : ge))}>
                                      Disconnect
                                    </Button>
                                    <Button size="small" variant="outlined" disabled={entry.testing || !entry.customer_id || !entry.conversion_action_id}
                                      onClick={async () => {
                                        setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, testing: true, test_result: null } : ge));
                                        try {
                                          const result = await postbacksService.testGoogleAds({
                                            login_customer_id: entry.login_customer_id || undefined,
                                            customer_id: entry.customer_id,
                                            conversion_action_id: entry.conversion_action_id,
                                            refresh_token: entry.refresh_token,
                                          });
                                          setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, testing: false, test_result: {
                                            success: result.success,
                                            message: result.success ? 'Connection successful' : (result.data?.error || 'Test failed'),
                                          } } : ge));
                                        } catch (err: any) {
                                          setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, testing: false, test_result: {
                                            success: false, message: err.response?.data?.message || err.message || 'Test failed',
                                          } } : ge));
                                        }
                                      }}>
                                      {entry.testing ? <CircularProgress size={16} /> : 'Test Connection'}
                                    </Button>
                                  </>
                                ) : (
                                  <Button size="small" variant="outlined"
                                    onClick={() => {
                                      window.open('/api/v1/auth/google/connect', 'google_connect', 'width=500,height=600,popup=yes');
                                      const handler = (event: MessageEvent) => {
                                        if (event.origin !== window.location.origin) return;
                                        if (event.data?.type !== 'google_connect') return;
                                        window.removeEventListener('message', handler);
                                        if (event.data.success && event.data.refresh_token) {
                                          setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, refresh_token: event.data.refresh_token } : ge));
                                          enqueueSnackbar('Google account connected', { variant: 'success' });
                                        } else {
                                          enqueueSnackbar(event.data.error || 'Google connect failed', { variant: 'error' });
                                        }
                                      };
                                      window.addEventListener('message', handler);
                                    }}>
                                    Connect Google Account
                                  </Button>
                                )}
                              </Box>
                              {entry.test_result && (
                                <Alert severity={entry.test_result.success ? 'success' : 'error'} sx={{ py: 0 }}
                                  onClose={() => setGoogleEntries(prev => prev.map(ge => ge._id === entry._id ? { ...ge, test_result: null } : ge))}>
                                  {entry.test_result.message}
                                </Alert>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      ))}

                      <Button variant="outlined" size="small" startIcon={<Add />} fullWidth
                        onClick={() => setGoogleEntries(prev => [...prev, {
                          _id: crypto.randomUUID(), event_types: [],
                          login_customer_id: '', customer_id: '', conversion_action_id: '', refresh_token: '',
                          expanded: true, testing: false, test_result: null,
                        }])}>
                        Add Google Ads Entry
                      </Button>
                    </>
                  )}

                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting || !workspaceId}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
